import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NotificationStatus } from '@shared/enums/notification-status';
import { buildQueryFilter } from '@shared/helpers/build-query-filter';
import { AppNotificationService, HeaderService } from '@shared/services';
import { Filter, QueryOptions } from 'odata-query';
import { catchError, map, Observable, switchMap, throwError } from 'rxjs';
import {
  CountryResponseWithNestedObjects,
  CreateGeneralCountryInformationRequest,
  GeneralCountryInformationTypeResponse,
  GeneralCountryInformationWithNestedObjectsResponse,
  GeneralCountryInformationWithNestedObjectsResponseODataWithMetadataResponse,
  GstarClient,
  UpdateGeneralCountryInformationRequest,
} from '../../../web-api-client';
import { mapCountryFromResponse } from '../../admin/mappers';
import { Country } from '../../admin/models';
import { mapGeneralInformationFromResponse } from '../mappers/map-general-information-from-response';
import { mapInformationTypeFromResponse } from '../mappers/map-information-type-from-response';
import { GeneralInformation } from '../models/general-information';
import { InformationType } from '../models/information-type';

type CountryInfoQuery =
  QueryOptions<GeneralCountryInformationWithNestedObjectsResponse>;

@Injectable({
  providedIn: 'root',
})
export class CountryInformationService {
  private readonly gstarClient = inject(GstarClient);
  private readonly notificationService = inject(AppNotificationService);
  private readonly translate = inject(TranslateService);
  private readonly headerService = inject(HeaderService);

  create(
    request: CreateGeneralCountryInformationRequest
  ): Observable<GeneralInformation[]> {
    return this.gstarClient.generalcountryinformationPOST(request).pipe(
      switchMap(() => {
        return this.getGeneralInformation(
          request.countryId,
          request.generalCountryInformationTypeId
        );
      })
    );
  }

  update(
    countryInformationId: number,
    request: UpdateGeneralCountryInformationRequest
  ): Observable<GeneralInformation[]> {
    return this.gstarClient
      .generalcountryinformationPUT(countryInformationId, request)
      .pipe(
        switchMap(() => {
          return this.getGeneralInformation(
            request.countryId,
            request.generalCountryInformationTypeId
          );
        })
      );
  }

  getCountryById(countryId: number): Observable<Country> {
    return this.gstarClient
      .countriesGET2(countryId)
      .pipe(
        map((country: CountryResponseWithNestedObjects) =>
          mapCountryFromResponse(country)
        )
      );
  }

  getGeneralInformation(
    countryId: number,
    typeId: number
  ): Observable<GeneralInformation[]> {
    const query: Partial<CountryInfoQuery> = {
      filter: this.countryInformationFilter(countryId, typeId),
    };
    return this.gstarClient
      .byclient2(
        this.headerService.selectedClient()?.id,
        undefined,
        buildQueryFilter(query),
        undefined,
        undefined,
        undefined,
        undefined,
        undefined
      )
      .pipe(
        catchError(() => this.errorHandler()),
        map(
          (
            result: GeneralCountryInformationWithNestedObjectsResponseODataWithMetadataResponse
          ) => {
            if (result.value?.length) {
              return mapGeneralInformationFromResponse(result.value[0]);
            }
            return [];
          }
        )
      );
  }

  getCountryInformationType(typeId: number): Observable<InformationType> {
    return this.gstarClient.generalcountryinformationtype(typeId).pipe(
      map((info: GeneralCountryInformationTypeResponse) => {
        return mapInformationTypeFromResponse(info);
      })
    );
  }

  private countryInformationFilter(
    countryId: number,
    typeId: number
  ): Filter<GeneralCountryInformationWithNestedObjectsResponseODataWithMetadataResponse> {
    return {
      'country/countryId': parseInt(countryId.toString()),
      'generalCountryInformationType/generalCountryInformationTypeId': parseInt(
        typeId.toString()
      ),
    };
  }

  private handleInvalidIds(): void {
    this.notificationService.notify(
      this.translate.instant('countryInformation.errorTitle'),
      this.translate.instant('countryInformation.errorDescription'),
      NotificationStatus.ERROR,
      {}
    );
  }

  private errorHandler(): Observable<never> {
    return throwError(() => {
      this.handleInvalidIds();
    });
  }
}
